// =============================================================================
// Client-side API for the Nachhilfe (tutoring) tracker. Solo, owner-only.
//
// Schema:
//   tutoring_students  (id, owner_id, name, default_rate, color, notes,
//                       archived, created_at)
//   tutoring_sessions  (id, owner_id, student_id, session_date,
//                       duration_minutes, rate, paid, paid_at, note,
//                       created_at)
//
// Semesters are derived from the session date — no DB row. We use German
// school Halbjahre:
//   1. HJ YYYY/YY+1 = 1 September YYYY  – 31 January YYYY+1
//   2. HJ YYYY/YY+1 = 1 February YYYY+1 – 31 August YYYY+1
//
// So a session on 12 Oct 2025 → "1. HJ 2025/26"; a session on 5 Aug 2025 →
// "2. HJ 2024/25" (still part of the school year that began Sep 2024).
// =============================================================================
import { supabase } from './supabase';

// -------- Semesters --------------------------------------------------------

// Resolve a JS Date or YYYY-MM-DD string into a semester descriptor.
// Returns: { id, label, half: 1|2, schoolYearStart: number, start: Date, end: Date }
export function semesterFor(dateLike) {
  const d = dateLike instanceof Date ? dateLike : new Date(`${dateLike}T00:00:00`);
  const month = d.getMonth() + 1; // 1..12
  const year = d.getFullYear();
  let schoolYearStart; // year in which the school year began (Sept)
  let half;            // 1 = Sept–Jan, 2 = Feb–Aug
  if (month >= 9) {
    schoolYearStart = year;
    half = 1;
  } else if (month <= 1) {
    schoolYearStart = year - 1;
    half = 1;
  } else {
    // Feb..Aug
    schoolYearStart = year - 1;
    half = 2;
  }
  const start = half === 1
    ? new Date(schoolYearStart, 8, 1)            // 1 Sept
    : new Date(schoolYearStart + 1, 1, 1);       // 1 Feb
  const end = half === 1
    ? new Date(schoolYearStart + 1, 0, 31)       // 31 Jan
    : new Date(schoolYearStart + 1, 7, 31);      // 31 Aug
  const yy = String((schoolYearStart + 1) % 100).padStart(2, '0');
  return {
    id: `${schoolYearStart}-${half}`,
    label: `${half}. HJ ${schoolYearStart}/${yy}`,
    half,
    schoolYearStart,
    start,
    end
  };
}

export function currentSemester() {
  return semesterFor(new Date());
}

// Walk a list of sessions and return all unique semester descriptors,
// most-recent first.
export function listSemestersFor(sessions) {
  const map = new Map();
  for (const s of sessions) {
    const sem = semesterFor(s.sessionDate);
    if (!map.has(sem.id)) map.set(sem.id, sem);
  }
  // Always include the current semester even if it has no sessions yet.
  const cur = currentSemester();
  if (!map.has(cur.id)) map.set(cur.id, cur);
  return [...map.values()].sort((a, b) => {
    if (a.schoolYearStart !== b.schoolYearStart) {
      return b.schoolYearStart - a.schoolYearStart;
    }
    return b.half - a.half;
  });
}

// -------- Students ---------------------------------------------------------

export async function listStudents({ includeArchived = false } = {}) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];
  let q = supabase
    .from('tutoring_students')
    .select('id, name, default_rate, color, notes, archived, created_at')
    .order('archived', { ascending: true })
    .order('name', { ascending: true });
  if (!includeArchived) q = q.eq('archived', false);
  const { data, error } = await q;
  if (error) throw error;
  return (data || []).map(mapStudent);
}

export async function createStudent({ name, defaultRate = 0, color = null, notes = null }) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not signed in');
  const trimmed = (name || '').trim();
  if (!trimmed) throw new Error('Name ist nötig');
  const { data, error } = await supabase
    .from('tutoring_students')
    .insert({
      owner_id: user.id,
      name: trimmed,
      default_rate: Number(defaultRate) || 0,
      color: color || null,
      notes: notes || null
    })
    .select('id, name, default_rate, color, notes, archived, created_at')
    .single();
  if (error) throw error;
  return mapStudent(data);
}

export async function updateStudent(id, patch) {
  const row = {};
  if (patch.name !== undefined) {
    const t = (patch.name || '').trim();
    if (!t) throw new Error('Name ist nötig');
    row.name = t;
  }
  if (patch.defaultRate !== undefined) row.default_rate = Number(patch.defaultRate) || 0;
  if (patch.color !== undefined) row.color = patch.color || null;
  if (patch.notes !== undefined) row.notes = patch.notes || null;
  if (patch.archived !== undefined) row.archived = !!patch.archived;
  if (Object.keys(row).length === 0) return;
  const { error } = await supabase
    .from('tutoring_students')
    .update(row)
    .eq('id', id);
  if (error) throw error;
}

export async function deleteStudent(id) {
  const { error } = await supabase
    .from('tutoring_students')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

function mapStudent(r) {
  return {
    id: r.id,
    name: r.name,
    defaultRate: Number(r.default_rate),
    color: r.color || null,
    notes: r.notes || '',
    archived: !!r.archived,
    createdAt: r.created_at
  };
}

// -------- Sessions ---------------------------------------------------------

export async function listSessions({ studentId } = {}) {
  let q = supabase
    .from('tutoring_sessions')
    .select('id, student_id, session_date, duration_minutes, rate, paid, paid_at, note, created_at')
    .order('session_date', { ascending: false })
    .order('created_at', { ascending: false });
  if (studentId) q = q.eq('student_id', studentId);
  const { data, error } = await q;
  if (error) throw error;
  return (data || []).map(mapSession);
}

export async function addSession({ studentId, sessionDate, durationMinutes, rate, note = null }) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not signed in');
  if (!studentId) throw new Error('Schüler wählen');
  const dur = Math.round(Number(durationMinutes));
  if (!Number.isFinite(dur) || dur <= 0) throw new Error('Dauer ungültig');
  const r = Number(rate);
  if (!Number.isFinite(r) || r < 0) throw new Error('Stundensatz ungültig');
  const { data, error } = await supabase
    .from('tutoring_sessions')
    .insert({
      owner_id: user.id,
      student_id: studentId,
      session_date: sessionDate,
      duration_minutes: dur,
      rate: r,
      note: (note || '').trim() || null
    })
    .select('id, student_id, session_date, duration_minutes, rate, paid, paid_at, note, created_at')
    .single();
  if (error) throw error;
  return mapSession(data);
}

export async function updateSession(id, patch) {
  const row = {};
  if (patch.sessionDate !== undefined) row.session_date = patch.sessionDate;
  if (patch.durationMinutes !== undefined) {
    const dur = Math.round(Number(patch.durationMinutes));
    if (!Number.isFinite(dur) || dur <= 0) throw new Error('Dauer ungültig');
    row.duration_minutes = dur;
  }
  if (patch.rate !== undefined) {
    const r = Number(patch.rate);
    if (!Number.isFinite(r) || r < 0) throw new Error('Stundensatz ungültig');
    row.rate = r;
  }
  if (patch.note !== undefined) row.note = (patch.note || '').trim() || null;
  if (patch.paid !== undefined) {
    row.paid = !!patch.paid;
    row.paid_at = patch.paid ? new Date().toISOString() : null;
  }
  if (Object.keys(row).length === 0) return;
  const { error } = await supabase
    .from('tutoring_sessions')
    .update(row)
    .eq('id', id);
  if (error) throw error;
}

export async function deleteSession(id) {
  const { error } = await supabase
    .from('tutoring_sessions')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

function mapSession(r) {
  return {
    id: r.id,
    studentId: r.student_id,
    sessionDate: r.session_date,
    durationMinutes: Number(r.duration_minutes),
    rate: Number(r.rate),
    paid: !!r.paid,
    paidAt: r.paid_at,
    note: r.note || '',
    createdAt: r.created_at
  };
}

// -------- Payments ---------------------------------------------------------

export async function listPayments({ studentId } = {}) {
  let q = supabase
    .from('tutoring_payments')
    .select('id, student_id, amount, payment_date, note, created_at')
    .order('payment_date', { ascending: false })
    .order('created_at', { ascending: false });
  if (studentId) q = q.eq('student_id', studentId);
  const { data, error } = await q;
  if (error) throw error;
  return (data || []).map(mapPayment);
}

export async function addPayment({ studentId, amount, paymentDate, note = null }) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not signed in');
  if (!studentId) throw new Error('Schüler wählen');
  const amt = Number(amount);
  if (!Number.isFinite(amt) || amt <= 0) throw new Error('Betrag ungültig');
  const { data, error } = await supabase
    .from('tutoring_payments')
    .insert({
      owner_id: user.id,
      student_id: studentId,
      amount: amt,
      payment_date: paymentDate,
      note: (note || '').trim() || null
    })
    .select('id, student_id, amount, payment_date, note, created_at')
    .single();
  if (error) throw error;
  return mapPayment(data);
}

export async function updatePayment(id, patch) {
  const row = {};
  if (patch.amount !== undefined) {
    const amt = Number(patch.amount);
    if (!Number.isFinite(amt) || amt <= 0) throw new Error('Betrag ungültig');
    row.amount = amt;
  }
  if (patch.paymentDate !== undefined) row.payment_date = patch.paymentDate;
  if (patch.note !== undefined) row.note = (patch.note || '').trim() || null;
  if (Object.keys(row).length === 0) return;
  const { error } = await supabase
    .from('tutoring_payments')
    .update(row)
    .eq('id', id);
  if (error) throw error;
}

export async function deletePayment(id) {
  const { error } = await supabase
    .from('tutoring_payments')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

function mapPayment(r) {
  return {
    id: r.id,
    studentId: r.student_id,
    amount: Number(r.amount),
    paymentDate: r.payment_date,
    note: r.note || '',
    createdAt: r.created_at
  };
}

// -------- Aggregations -----------------------------------------------------

// Returns { hours, earned, paid, outstanding, sessions: [...] } for one
// semester (or all if `semester` is null).
export function summarise(sessions, semester = null) {
  let minutes = 0;
  let earned = 0;
  let paid = 0;
  const out = [];
  for (const s of sessions) {
    if (semester) {
      const sem = semesterFor(s.sessionDate);
      if (sem.id !== semester.id) continue;
    }
    minutes += s.durationMinutes;
    const value = (s.durationMinutes / 60) * s.rate;
    earned += value;
    if (s.paid) paid += value;
    out.push(s);
  }
  return {
    hours: minutes / 60,
    earned,
    paid,
    outstanding: earned - paid,
    sessions: out
  };
}

// Helper: monetary value of one session.
export const sessionValue = (s) => (s.durationMinutes / 60) * s.rate;

// Running balance per student (all-time, ignoring semesters).
//   balance = sum(payments) - sum(unpaid sessions)
//   • > 0  → parent has credit (prepaid)
//   • < 0  → parent owes me
//   • Sessions explicitly marked `paid` are treated as already-settled cash
//     and don't enter the running balance at all.
export function computeStudentBalance(sessions, payments) {
  let owed = 0;
  for (const s of sessions) {
    if (!s.paid) owed += sessionValue(s);
  }
  let received = 0;
  for (const p of payments) received += p.amount;
  return {
    earnedUnpaid: owed,
    received,
    balance: received - owed
  };
}

// Build a chronological timeline (oldest → newest) of sessions and
// payments, with the running balance after each event. Useful for the
// per-student detail view.
export function buildTimeline(sessions, payments, { semester = null } = {}) {
  const events = [];
  for (const s of sessions) {
    if (semester && semesterFor(s.sessionDate).id !== semester.id) continue;
    events.push({
      kind: 'session',
      date: s.sessionDate,
      createdAt: s.createdAt,
      session: s,
      delta: s.paid ? 0 : -sessionValue(s)
    });
  }
  for (const p of payments) {
    if (semester && semesterFor(p.paymentDate).id !== semester.id) continue;
    events.push({
      kind: 'payment',
      date: p.paymentDate,
      createdAt: p.createdAt,
      payment: p,
      delta: p.amount
    });
  }
  events.sort((a, b) => {
    if (a.date !== b.date) return String(a.date).localeCompare(String(b.date));
    return String(a.createdAt || '').localeCompare(String(b.createdAt || ''));
  });
  let running = 0;
  for (const e of events) {
    running += e.delta;
    e.runningBalance = running;
  }
  return events;
}
